import { useCallback, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'

import { ActionSelections } from '../../core/types/chat.types'
import { ExploreCatalogCard as ExploreCatalogCardType } from '../../core/types/explore.types'
import ExploreChartIcon from '../icons/explore-chart-icon'
import ExploreCatalogFilters from '../explore/explore-catalog-filters'
import ExploreCatalogView from '../explore/explore-catalog-view'
import ExploreCardInitiatorDialog from '../explore/explore-card-initiator-dialog'

type ExploreEmptyStateProps = {
  isLoading?: boolean
  error?: string | null
  categories?: import('../../core/types/explore.types').ExploreCatalogCategory[]
  cards?: ExploreCatalogCardType[]
  filteredCards?: ExploreCatalogCardType[]
  selectedCategory?: string
  onSelectCategory?: (categoryId: string) => void
  onSelectCard?: (card: ExploreCatalogCardType) => void
  onReload?: () => void
  onApplyCard?: (card: ExploreCatalogCardType, selections?: Partial<ActionSelections>) => void
  initialSelections?: Partial<ActionSelections>
}

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflow: 'hidden',
    backgroundColor: 'var(--ds-bg-subtle, #f1f2f7)',
    height: '100%',
  },
  inner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: 1044,
    minHeight: 0,
    flex: 1,
    padding: '40px 24px 32px',
    height: '100%',
  },
  header: {
    marginBottom: 32,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  chartIcon: { width: 28, height: 28 },
  title: {
    fontSize: '1.75rem',
    fontWeight: 600,
    color: 'var(--ds-fg-primary, #1f2430)',
    margin: 0,
  },
  subtitle: {
    fontSize: '0.9375rem',
    fontWeight: 400,
    color: 'var(--ds-fg-tertiary, #8c92a5)',
    textAlign: 'center',
    margin: 0,
  },
  statePlaceholder: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    color: 'var(--ds-fg-tertiary, #8c92a5)',
    fontSize: '0.875rem',
  },
  retryButton: {
    borderRadius: 9999,
    padding: '8px 16px',
    fontSize: '0.8125rem',
    fontWeight: 500,
    background: '#d73f2e',
    color: '#fff',
    border: 0,
    cursor: 'pointer',
    transition: 'background 200ms ease',
    '&:hover': { background: '#c2311f' },
  },
  catalogContent: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    flex: 1,
    minHeight: 0,
  },
  pendingDialog: {
    flexShrink: 0,
    borderTop: '1px solid var(--ds-border-default, #d7dae4)',
    background: 'var(--ds-bg-overlay, rgba(255,255,255,0.92))',
    padding: '16px 24px',
    marginTop: theme.spacing(1),
  },
}))

const ExploreEmptyState = ({
  isLoading = false,
  error = null,
  categories = [],
  cards = [],
  filteredCards,
  selectedCategory = 'all',
  onSelectCategory,
  onSelectCard,
  onReload,
  onApplyCard,
  initialSelections,
}: ExploreEmptyStateProps) => {
  const classes = useStyles()
  const [pendingCard, setPendingCard] = useState<ExploreCatalogCardType | null>(null)

  const displayCards = filteredCards ?? (selectedCategory === 'all' ? cards : cards.filter((c) => c.categoryId === selectedCategory))

  const handleCardSelected = useCallback((card: ExploreCatalogCardType) => {
    if (card.initiation.type === 'menu') {
      onSelectCard?.(card)
      return
    }

    if (!card.initiation.requiredCondition) {
      onSelectCard?.(card)
      return
    }

    setPendingCard(card)
  }, [onSelectCard])

  const handleCancelPending = useCallback(() => setPendingCard(null), [])

  const handleApplyPending = useCallback((selections: Partial<ActionSelections>) => {
    if (!pendingCard) return
    onApplyCard?.(pendingCard, selections)
    setPendingCard(null)
  }, [pendingCard, onApplyCard])

  const hasCards = categories.length > 0 || cards.length > 0

  return (
    <div className={classes.root}>
      <div className={classes.inner}>
        <div className={classes.header}>
          <div className={classes.titleRow}>
            <ExploreChartIcon className={classes.chartIcon} />
            <h2 className={classes.title}>Explore Data Mart</h2>
          </div>
          <p className={classes.subtitle}>
            Query the Prodigy Data Mart with guided point-and-click workflows
          </p>
        </div>

        {isLoading ? (
          <div className={classes.statePlaceholder}>
            <span>Loading queries…</span>
          </div>
        ) : error ? (
          <div className={classes.statePlaceholder}>
            <span>Failed to load Explore queries.</span>
            {onReload && (
              <button type="button" className={classes.retryButton} onClick={onReload}>
                Retry
              </button>
            )}
          </div>
        ) : !hasCards ? (
          <div className={classes.statePlaceholder}>
            <span>No queries available for your account.</span>
          </div>
        ) : (
          <div className={classes.catalogContent}>
            {categories.length > 0 && onSelectCategory && (
              <ExploreCatalogFilters
                categories={categories}
                selectedCategory={selectedCategory}
                onSelectCategory={onSelectCategory}
              />
            )}

            <ExploreCatalogView
              cards={displayCards}
              onSelectCard={handleCardSelected}
            />

            {pendingCard && (
              <div className={classes.pendingDialog}>
                <ExploreCardInitiatorDialog
                  card={pendingCard}
                  initialSelections={initialSelections}
                  onCancel={handleCancelPending}
                  onApply={handleApplyPending}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ExploreEmptyState
